/**
 * Info message block of the ABCDior Settings screen. Mounted once the current
 * message is known, so the fields start from the server values.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PLATFORM, type Platform } from '@/shared/constants';
import { snackbar } from '@/shared/ui/snackbar';
import { hasError, infoMessageQueryOptions, updateInfoMessage } from './api';
import type { InfoMessage } from './info-message';
import { MESSAGE_LEVELS } from './logic';
import { Select } from '@/shared/ui/Select';

interface InfoMessageFormProps {
  platform: Platform;
  initial: InfoMessage | null;
}

const errorText = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

/** Initial target of the message: DMS is front office only, otherwise the stored value ("all" when nothing is stored). */
const initialDisplayOn = (
  isDms: boolean,
  seed: { displayOn?: string | undefined } | null,
): string => {
  if (isDms) return 'fo';
  if (seed) return seed.displayOn ?? '';
  return 'all';
};

export function InfoMessageForm({
  platform,
  initial,
}: Readonly<InfoMessageFormProps>) {
  const isDms = platform === PLATFORM.DMS;
  const queryClient = useQueryClient();
  // The form starts from (false, '', 'grey', 'all') then copies the API answer
  // field by field, even when the answer is `{}` (no message stored yet for the
  // platform): in that case nothing is checked and no level is selected (see migration.md).
  const seed = initial && !initial.error ? initial : null;

  const [isEnabled, setIsEnabled] = useState(
    seed ? seed.isEnabled === true : false,
  );
  const [message, setMessage] = useState(seed ? (seed.message ?? '') : '');
  const [level, setLevel] = useState<string>(
    seed ? (seed.level ?? '') : 'grey',
  );
  // DMS has no back office of its own yet: the message can only target the front office.
  const [displayOn, setDisplayOn] = useState(initialDisplayOn(isDms, seed));
  const [threadFilterEnabled, setThreadFilterEnabled] = useState(
    seed?.threadFilterEnabled === true,
  );

  const save = useMutation({
    mutationFn: () =>
      updateInfoMessage({
        isEnabled,
        message,
        level,
        displayOn,
        platform,
        threadFilterEnabled,
      }),
    onSuccess: (data) => {
      if (hasError(data))
        snackbar(`An error occurred while saving the message: ${data.error}`);
      else snackbar('Successfully saved the info message');
      void queryClient.invalidateQueries({
        queryKey: infoMessageQueryOptions(platform).queryKey,
      });
    },
    onError: (error) => {
      console.error(error);
      snackbar(
        `An error occurred while saving the message: ${errorText(error)}`,
      );
    },
  });

  return (
    <>
      <div className="flex justify-center">
        <div className="styled-input-square styled-input-single">
          <input
            type="checkbox"
            name="isEnabled"
            id="is-enabled"
            data-testid="settings-message-enabled"
            checked={isEnabled}
            onChange={(event) => setIsEnabled(event.target.checked)}
          />
          <label htmlFor="is-enabled">Enabled</label>
        </div>
      </div>
      <div className="bs-row">
        <div className="col-span-8">
          <textarea
            className="field"
            name="message"
            placeholder="Info message"
            aria-label="Info message"
            data-testid="settings-message-text"
            style={{ height: 130 }}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
          />
          <button
            className="button button-form"
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            data-testid="settings-message-save-button"
          >
            Save
          </button>
        </div>
        <div className="col-span-4">
          <div className="mb-2">
            <label htmlFor="info-level">Message level:</label>
            <div className="field field-selector">
              <Select
                id="info-level"
                name="level"
                placeholder="--Select level--"
                options={MESSAGE_LEVELS.map((item) => ({
                  value: item.value,
                  label: item.name,
                }))}
                value={level}
                onChange={setLevel}
                testId="settings-message-level"
              />
            </div>
          </div>

          <div>Enable thread filters:</div>
          <div className="styled-input-square styled-input-single">
            <input
              type="checkbox"
              name="threadFilterEnabled"
              id="threadFilterEnabled"
              data-testid="settings-message-thread-filter-enabled"
              checked={threadFilterEnabled}
              onChange={(event) => setThreadFilterEnabled(event.target.checked)}
            />
            <label htmlFor="threadFilterEnabled">Enabled</label>
          </div>

          <div>Display on:</div>
          <div className="styled-input-square mt-1">
            <fieldset disabled={isDms}>
              {!isDms && (
                <div className="styled-input-single">
                  <input
                    type="radio"
                    name="displayOn"
                    value="all"
                    id="checkbox-both-platforms"
                    data-testid="settings-message-display-on-all"
                    checked={displayOn === 'all'}
                    onChange={() => setDisplayOn('all')}
                  />
                  <label htmlFor="checkbox-both-platforms">
                    Both platforms
                  </label>
                </div>
              )}
              {!isDms && (
                <div className="styled-input-single">
                  <input
                    type="radio"
                    name="displayOn"
                    value="bo"
                    id="checkbox-bo-only"
                    data-testid="settings-message-display-on-bo"
                    checked={displayOn === 'bo'}
                    onChange={() => setDisplayOn('bo')}
                  />
                  <label htmlFor="checkbox-bo-only">BO only</label>
                </div>
              )}
              <div className="styled-input-single">
                <input
                  type="radio"
                  name="displayOn"
                  value="fo"
                  id="checkbox-fo-only"
                  data-testid="settings-message-display-on-fo"
                  checked={displayOn === 'fo'}
                  onChange={() => setDisplayOn('fo')}
                />
                <label htmlFor="checkbox-fo-only">FO only</label>
              </div>
            </fieldset>
          </div>
        </div>
      </div>
    </>
  );
}
